import React from "react";
import { X, Trash2, ChevronRight } from "lucide-react";
import { MetricWidgetConfigForm } from "./widgets/MetricWidgetConfig";
import { ChartWidgetConfigForm } from "./widgets/ChartWidgetConfig";
import { TextWidgetConfigForm } from "./widgets/TextWidgetConfig";
import { MediaWidgetConfigForm } from "./widgets/MediaWidgetConfig";
import { IFrameWidgetConfigForm } from "./widgets/IFrameWidgetConfig";
import { TableWidgetConfigForm } from "./widgets/TableWidgetConfig";

interface WidgetConfigSidebarProps {
  widgetId: string;
  widgetType: string;
  currentConfig: any;
  onDone: () => void;
  onDelete: () => void;
  onCollapse: () => void;
  onConfigChange: (config: any) => void;
  moduleId?: string; // Story 6.2: For scoping data sources to module sheets
}

export function WidgetConfigSidebar({
  widgetId: _widgetId,
  widgetType,
  currentConfig,
  onDone,
  onDelete,
  onCollapse,
  onConfigChange,
  moduleId: _moduleId, // Story 6.2: Will be used for sheet filtering in future
}: WidgetConfigSidebarProps) {
  const handleDelete = () => {
    if (confirm("Delete this widget?")) {
      onDelete();
    }
  };

  return (
    <div className="w-72 bg-gray-50 dark:bg-gray-900 border-l border-gray-200 dark:border-gray-700 p-4 flex flex-col h-full">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300">
          {widgetType.charAt(0).toUpperCase() + widgetType.slice(1)} Settings
        </h3>
        <div className="flex gap-1">
          <button
            onClick={onCollapse}
            className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
            title="Collapse sidebar"
          >
            <ChevronRight size={16} />
          </button>
          <button
            onClick={onDone}
            className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
            title="Close"
          >
            <X size={16} />
          </button>
        </div>
      </div>

      {/* Widget-Specific Config Form */}
      {widgetType === "metric" ? (
        <MetricWidgetConfigForm
          initialConfig={currentConfig}
          onChange={onConfigChange}
        />
      ) : widgetType === "chart" ? (
        <ChartWidgetConfigForm
          initialConfig={currentConfig}
          onChange={onConfigChange}
        />
      ) : widgetType === "text" ? (
        <TextWidgetConfigForm
          initialConfig={currentConfig}
          onChange={onConfigChange}
        />
      ) : widgetType === "media" ? (
        <MediaWidgetConfigForm
          initialConfig={currentConfig}
          onChange={onConfigChange}
        />
      ) : widgetType === "iframe" ? (
        <IFrameWidgetConfigForm
          initialConfig={currentConfig}
          onChange={onConfigChange}
        />
      ) : widgetType === "table" ? (
        <TableWidgetConfigForm
          initialConfig={currentConfig}
          onChange={onConfigChange}
        />
      ) : (
        <div className="flex-1 flex flex-col items-center justify-center p-6 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
          <p className="text-sm text-blue-800 dark:text-blue-200 text-center mb-4">
            ⚙️ {widgetType} widget configuration coming in future stories
          </p>
        </div>
      )}

      {/* Actions */}
      <div className="mt-4 space-y-2">
        <button
          onClick={handleDelete}
          className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg font-semibold transition-all"
        >
          <Trash2 size={16} />
          Delete Widget
        </button>

        <button
          onClick={onDone}
          className="w-full px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-semibold transition-all"
        >
          Done
        </button>
      </div>
    </div>
  );
}
